import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map } from 'rxjs';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthStateService } from '../../../core/auth/auth-state.service';
import { NumeronCardComponent } from '../../../shared/ui/numeron-card/numeron-card.component';
import { NumeronCollectionModel, NumeronItemModel } from '../models/numeron.model';
import { NumeronsService } from '../services/numerons.service';

@Component({
  selector: 'app-manage-numeron-page',
  imports: [NumeronCardComponent],
  templateUrl: './manage-numeron-page.component.html',
})
export class ManageNumeronPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly numeronsService = inject(NumeronsService);
  private readonly authStateService = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly collectionId = signal('');
  protected readonly returnTo = signal('/mynumerons');
  protected readonly manageReturnTo = signal('/mynumerons');
  protected readonly isLoading = signal(true);
  protected readonly loadErrorMessage = signal('');
  protected readonly loadedCollection = signal<NumeronCollectionModel | null>(null);

  public constructor() {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([paramMap, queryParamMap]) => ({
          collectionId: paramMap.get('collectionId') ?? '',
          returnTo: this.resolveReturnTo(queryParamMap.get('returnTo')),
        })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ collectionId, returnTo }) => {
        this.collectionId.set(collectionId);
        this.returnTo.set(returnTo);
        this.manageReturnTo.set(this.resolveManageReturnTo(collectionId, returnTo));
        this.title.setTitle('Manage Numeron');
        this.ga4AnalyticsService.trackPageView({
          contentId: collectionId || undefined,
          contentType: 'Manage Numeron',
          contentGroup: 'My Numerons',
        });
        this.loadCollection(collectionId);
      });
  }

  protected canManage(): boolean {
    return !this.isLoading() && this.loadErrorMessage() === '' && this.loadedCollection() !== null;
  }

  protected numeron(): NumeronItemModel | null {
    return this.loadedCollection()?.numerons[0] ?? null;
  }

  private loadCollection(collectionId: string): void {
    this.isLoading.set(true);
    this.loadErrorMessage.set('');
    this.loadedCollection.set(null);

    if (!collectionId) {
      this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.numeronsService
      .getMyNumeronCollection(collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          const numeron = collection.numerons[0];
          if (!numeron) {
            this.loadErrorMessage.set(
              'Numeron details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isPublisherMatch(collection, numeron)) {
            this.loadErrorMessage.set('You can manage only Numerons published by your account.');
            this.isLoading.set(false);
            return;
          }

          this.loadedCollection.set(collection);
          this.title.setTitle(numeron.title?.trim() || 'Manage Numeron');
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set('Numeron details are unavailable right now. Please try again.');
          this.isLoading.set(false);
        },
      });
  }

  private resolveReturnTo(returnToParam: string | null): string {
    const returnTo = returnToParam?.trim() ?? '';
    return returnTo.startsWith('/mynumerons') ? returnTo : '/mynumerons';
  }

  private resolveManageReturnTo(collectionId: string, returnTo: string): string {
    const managePath = `/mynumerons/numeron/${collectionId}`;
    return returnTo === '/mynumerons'
      ? managePath
      : `${managePath}?returnTo=${encodeURIComponent(returnTo)}`;
  }

  private isPublisherMatch(collection: NumeronCollectionModel, numeron: NumeronItemModel): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const collectionPublisherId = collection.user?._id?.trim() ?? '';
    const numeronPublisherId = numeron.user?._id?.trim() ?? '';
    const collectionPublisherName = collection.user?.displayName?.trim() ?? '';
    const numeronPublisherName = numeron.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return (
        authenticatedPublisherId === collectionPublisherId ||
        authenticatedPublisherId === numeronPublisherId
      );
    }

    if (authenticatedDisplayName) {
      return (
        authenticatedDisplayName === collectionPublisherName ||
        authenticatedDisplayName === numeronPublisherName
      );
    }

    return true;
  }
}
